import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  Modal,
  Tab,
  Box,
  IconButton,
  Tooltip,
  Button,
  Alert,
} from "@mui/material";
import styled from "styled-components";
import { Helmet } from "react-helmet-async";
import { Users, UserX, AlertTriangle } from "react-feather";
import useTeamMembers from "../../../swr/hooks/Team/useTeamMembers";
import TeamDataTable from "./TeamTable/TeamDataTable";
import InviteTeamMember from "./InviteTeamMember";
import PrivateHeader from "../../../components/PrivateHeader";
import LoaderWrapper from "../../../components/Loaders/LoaderWrapper";
import OnLoadError from "../../../components/UI/OnLoadError";
import TabPanel from "../../../components/TabPanel";
import OutstandingInvitationsTable from "./OutstandingInvitationsTable";
import PendingTable from "./PendingTable";
import { TableTabs, TableTabBar } from "../styles";
import BadgeLabel from "../../../components/BadgeLabel";
import { Plus } from "react-feather";
import CreateButton from "../../../components/UI/CreateButton";
import LimitsMsg from "../../../components/LimitsMsg";
import useTeamInvitations from "src/swr/hooks/Team/useTeamInvitations";
import FormerTeamMembers from "./FormerTeamMembers";
import useGlobalStore from "src/store/useGlobalStore";
import { JOB_TYPES } from "src/storage/constants";

const InviteTeamMemberViaCsv = lazy(() => import("./InviteTeamMembersViaCsv"));

const ButtonHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
`;

const StyledIconButtton = styled(IconButton)`
  border-radius: 6px;
  max-height: 36px;
  max-width: 36px;
  padding: 8px;
  background: #eeeeee80;
  border: ${(props) =>
    props?.$disabled ? "1px solid #eeeeee" : "1px solid lightgray"};

  svg {
    color: ${(props) => (props?.$disabled ? "lightgray" : "slategrey")};
  }

  &:hover {
    background: #eeeeee;
  }
`;

const InactiveWarning = () => (
  <Alert
    sx={{
      borderRadius: 1,
      border: "1px solid gainsboro",
      background: "#eeeeee90",
    }}
    icon={<AlertTriangle height={20} color="firebrick" />}
  >
    <p style={{ fontWeight: 500 }}>Multiple Inactive team members</p>
    <p>
      Consider removing these team members if they are no longer with your
      company. Sort the 'Last Submission' column to view inactive team members.
    </p>
  </Alert>
);

const TeamMembers = ({ location }) => {
  const config = useGlobalStore((state) => state.config);

  const [inviteMemberModal, setInviteMemberModal] = useState(false);
  const [inviteCsvModal, setInviteCsvModal] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [formerTeamMembersOpen, setFormerTeamMembersOpen] = useState(false);

  const { isLoading, isError, mutate: teamMutate, team } = useTeamMembers();
  const {
    isLoading: invitesLoading,
    mutate: invitesMutate,
    data: invitesData,
  } = useTeamInvitations();

  const { user } = config;
  const { job } = user;
  const { read_only: readOnly } = user;
  const { permissions } = config?.current_company;

  const pendingTeamMembers =
    config?.current_company?.pending_access_requests?.team_members;

  const isManager = job === JOB_TYPES.SUPERVISOR;

  useEffect(() => {
    if (location.state === null || !location.state) return;
    setTabValue(2);
  }, []);

  if (isLoading || invitesLoading) {
    return (
      <>
        <PrivateHeader header="Team Members" Icon={Users} />
        <LoaderWrapper text="Team Members" />
      </>
    );
  }

  if (isError) {
    return <OnLoadError />;
  }

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  const numInvites = invitesData?.invitations?.length || 0;
  const numTeamMembers = team?.length || 0;

  return (
    <>
      <Helmet title="Spiffy | Team Members" />
      <PrivateHeader header="Team Members" Icon={Users}>
        <LimitsMsg
          title="Team Member"
          current={numTeamMembers + numInvites}
          limit={permissions?.fields?.user_limit}
          explanationText={`Your account allows up to ${permissions?.fields?.user_limit} team members. To add more, remove members or upgrade your plan.`}
        />
      </PrivateHeader>
      <ButtonHolder>
        <CreateButton
          disabled={readOnly}
          handleClick={() => setInviteMemberModal(true)}
          title="Invite Team Member"
        />
        <Button
          disabled={readOnly}
          color="inherit"
          variant="outlined"
          style={{ height: 38, borderRadius: 4 }}
          startIcon={<Plus height={14} width={14} />}
          onClick={() => setInviteCsvModal(true)}
        >
          Invite via CSV
        </Button>
        <div style={{ marginLeft: "auto" }}>
          <span>
            <Tooltip arrow title="View Former Team Members">
              <StyledIconButtton
                disabled={readOnly || isManager}
                $disabled={readOnly || isManager}
                onClick={() => setFormerTeamMembersOpen(true)}
              >
                <UserX height={16} width={16} />
              </StyledIconButtton>
            </Tooltip>
          </span>
        </div>
      </ButtonHolder>
      <div style={{ height: 18 }} />

      <TableTabBar position="relative" elevation={0}>
        <TableTabs
          value={tabValue}
          onChange={handleTabChange}
          scrollButtons={false}
          TabIndicatorProps={{
            style: { display: "none" },
          }}
        >
          <Tab label="Team Members" />
          <Tab label="Outstanding Invitations" />
          <Tab
            label={
              <BadgeLabel
                text="Access Requests"
                num={!pendingTeamMembers ? null : pendingTeamMembers}
              />
            }
          />
        </TableTabs>
      </TableTabBar>

      <Box>
        <TabPanel value={tabValue} index={0}>
          <TeamDataTable
            readOnly={readOnly}
            isManager={isManager}
            team={team}
            mutate={teamMutate}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <OutstandingInvitationsTable
            isLoading={invitesLoading}
            mutate={invitesMutate}
            data={invitesData}
            readOnly={readOnly}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <PendingTable readOnly={readOnly} teamMutate={teamMutate} />
        </TabPanel>
      </Box>
      <Modal
        open={Boolean(inviteMemberModal)}
        onClose={() => setInviteMemberModal(false)}
        aria-labelledby="Invite Team Member"
        aria-describedby="Modal to invite a team member"
      >
        <div>
          <InviteTeamMember setInviteMemberModal={setInviteMemberModal} />
        </div>
      </Modal>
      <Modal
        open={formerTeamMembersOpen}
        onClose={() => setFormerTeamMembersOpen(false)}
      >
        <div>
          <FormerTeamMembers />
        </div>
      </Modal>
      <Modal
        open={Boolean(inviteCsvModal)}
        onClose={() => setInviteCsvModal(false)}
        aria-labelledby="Invite Team Members By Csv"
        aria-describedby="Modal to invite Team Members By Csv"
      >
        <div>
          <Suspense fallback={<span />}>
            <InviteTeamMemberViaCsv
              mutate={teamMutate}
              setInviteCsvModal={setInviteCsvModal}
            />
          </Suspense>
        </div>
      </Modal>
    </>
  );
};

export default TeamMembers;
